import { Space as AntSpace, type SpaceProps as AntSpaceProps } from 'antd';
import { forwardRef } from 'react';

type CustomSize = 'none' | 'micro';
type SpaceSize = AntSpaceProps['size'] | CustomSize;

interface SpaceProps extends Omit<AntSpaceProps, 'size'> {
    size?: SpaceSize;
}

const CUSTOM_SIZE_MAP: Record<CustomSize, number> = {
    none: 0, // No gap between items
    micro: 4, // Maps to spacing.xs (4px)
};

function isCustomSize(size: SpaceSize | undefined): size is CustomSize {
    return typeof size === 'string' && size in CUSTOM_SIZE_MAP;
}

/**
 * Space component wrapper with additional size options.
 *
 * Extends Ant Design's Space with:
 * - `size="none"` - 0px gap (no spacing between items)
 * - `size="micro"` - 4px gap for tight layouts (table actions, icon+label)
 *
 * All standard Ant Design Space sizes are supported:
 * - `"small"` - 8px
 * - `"middle"` - 16px
 * - `"large"` - 24px
 * - Numeric values for custom gaps
 */
const InternalSpace = forwardRef<HTMLDivElement, SpaceProps>(({ size, direction, ...props }, ref) => {
    const resolvedSize: AntSpaceProps['size'] = isCustomSize(size) ? CUSTOM_SIZE_MAP[size] : size;

    return <AntSpace ref={ref} size={resolvedSize} orientation={direction} {...props} />;
});

InternalSpace.displayName = 'Space';

export const Space = Object.assign(InternalSpace, {
    Addon: AntSpace.Addon,
    Compact: AntSpace.Compact,
});

export type { SpaceProps };
